/* Spotlight: the home page below the film. One <antarti-spotlight-light> for the whole page
   (every Spotlight section includes this script; the second run is a no-op), everything else
   delegated, so a section the theme editor reloads, adds or moves just works (rescan on
   shopify:section:load / unload / reorder).
   - reveals: .rv gets .in as it enters (motion only; html.sl-on gates the CSS),
   - the follow-spot: one pool of moonlight resting on the row under the pointer or focus, else
     on the row or room lead ([data-lead]) nearest the reading line (45% of the screen), only
     when at least 80% of it is clear of the header and the screen's foot. It irises out and
     in between objects (180/260ms) and pans (450ms) between two rows of one group; the object
     it reaches is lit (.is-lit) and gleams once,
   - the ladder's runs of stones (layRungs, --k px per mm, stones under 40px simplified),
   - the spec sheet <dialog> (the row's <template>), wishlist hearts and Save,
   - the fire room's scroll (--beam, --fan) and its switch,
   - the gift note's counter and "Send a hint",
   - the sunrise: the sun's place (the i's axis, the countdown's height) and --sunrise.
   Reduced motion: no reveals, no scroll scrubs (the stills are drawn), instant cuts, no gleam. */

(() => {
  'use strict';

  if (customElements.get('antarti-spotlight-light')) return;

  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (x) => Math.min(1, Math.max(0, x));
  const smooth = (t) => t * t * (3 - 2 * t);
  const token = (n) => getComputedStyle(root).getPropertyValue(n).trim() || 'ease';
  const wl = () => window.antarti && window.antarti.wishlist;
  const ROWS = '.sl-rung, .sl-piece';
  const N = '1000000'; // the count placeholder the Liquid renders into plural strings

  class Light extends HTMLElement {
    connectedCallback() {
      root.classList.add('sl-on');
      this.className = 'sl-follow';
      this.setAttribute('aria-hidden', 'true');
      this.innerHTML = '<span class="sl-follow__pool"></span>';
      this.pool = this.firstChild;
      this.spot = { lead: null, phase: 'off', pending: undefined, x: 0, y: 0, from: null, t0: 0, lit: null };
      this.pointed = null;
      this.queued = false;
      this.frame = this.frame.bind(this);
      this.schedule = () => {
        if (this.queued) return;
        this.queued = true;
        this.raf = requestAnimationFrame(this.frame);
      };
      const later = () => requestAnimationFrame(() => this.rescan());
      const row = (e) => e.target.closest && e.target.closest(ROWS);
      this.on = [
        [window, 'scroll', this.schedule, { passive: true }],
        [window, 'resize', () => this.layout(), { passive: true }],
        [document, 'click', (e) => this.click(e)],
        [document, 'change', (e) => e.target.matches('[data-sl-fire]') && (e.target.closest('.sl-fire').dataset.stone = e.target.value)],
        [document, 'input', (e) => e.target.matches('[data-sl-note]') && this.count(e.target)],
        [document, 'antarti:wishlist', () => this.wish()],
        [document, 'pointerover', (e) => e.pointerType === 'mouse' && row(e) && this.point(row(e))],
        [document, 'pointerout', (e) => e.pointerType === 'mouse' && row(e) && this.unpoint(row(e), e.relatedTarget, 140)],
        [document, 'focusin', (e) => row(e) && this.point(row(e))],
        [document, 'focusout', (e) => row(e) && this.unpoint(row(e), e.relatedTarget, 0)],
        [document, 'shopify:section:load', later],
        [document, 'shopify:section:unload', later],
        [document, 'shopify:section:reorder', later],
      ];
      this.on.forEach(([t, type, fn, opt]) => t.addEventListener(type, fn, opt));
      this.rescan();
      document.fonts?.ready.then(() => this.layout());
    }

    disconnectedCallback() {
      this.on.forEach(([t, type, fn, opt]) => t.removeEventListener(type, fn, opt));
      this.io?.disconnect();
      cancelAnimationFrame(this.raf);
      clearTimeout(this.ot);
      root.classList.remove('sl-on');
    }

    rescan() {
      const q = (s) => [...document.querySelectorAll(s)];
      this.targets = q(`${ROWS}, [data-lead]`).filter((t) => !t.closest('antarti-seq'));
      this.fires = q('.sl-fire');
      this.suns = q('[data-sl-sun]');
      if (this.spot.lead && !this.spot.lead.isConnected) this.spot = { ...this.spot, lead: null, lit: null, phase: 'off', from: null };
      if (this.pointed && !this.pointed.isConnected) this.pointed = null;
      this.io?.disconnect();
      const rv = q('.rv:not(.in)');
      if (reduce.matches || !('IntersectionObserver' in window)) rv.forEach((el) => el.classList.add('in'));
      else {
        this.io = new IntersectionObserver(
          (es) => es.forEach((en) => en.isIntersecting && (en.target.classList.add('in'), this.io.unobserve(en.target))),
          { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
        );
        rv.forEach((el) => this.io.observe(el));
      }
      q('[data-sl-note]').forEach((n) => this.count(n));
      this.wish();
      this.layout();
    }

    layout() {
      // The ladder: one stone centred on each row (the one the dimension line measures), the rest at
      // a setting's pitch; a stone under 40px uses the simplified brilliant so it still reads.
      document.querySelectorAll('.sl-ladder').forEach((lad) => {
        const k = parseFloat(getComputedStyle(lad).getPropertyValue('--k')) || 12;
        lad.querySelectorAll('.sl-rung[data-mm] .sl-rung__run').forEach((svg) => {
          const d = Number(svg.closest('.sl-rung').dataset.mm) * k;
          const w = svg.clientWidth || 300;
          const pitch = d * 1.1, box = d / 0.85, n = Math.ceil(w / 2 / pitch) + 1;
          const id = lad.dataset.brill + (d < 40 ? '-m' : '');
          let h = '';
          for (let i = -n; i <= n; i++) {
            h += `<use href="#${id}" x="${(w / 2 + i * pitch - box / 2).toFixed(2)}" y="${(d / 2 - box / 2).toFixed(2)}" width="${box.toFixed(2)}" height="${box.toFixed(2)}"/>`;
          }
          svg.setAttribute('viewBox', `0 0 ${w.toFixed(1)} ${d}`);
          svg.innerHTML = h;
        });
      });
      this.suns?.forEach((st) => this.placeSky(st));
      this.schedule();
    }

    // ---- The follow-spot ----
    point(t) {
      clearTimeout(this.ot);
      this.pointed = t;
      this.schedule();
    }

    unpoint(t, to, wait) {
      if (this.pointed !== t || t.contains(to)) return;
      clearTimeout(this.ot);
      this.ot = setTimeout(() => {
        if (this.pointed === t && !t.contains(document.activeElement)) {
          this.pointed = null;
          this.schedule();
        }
      }, wait);
    }

    box(t) {
      const d = t.matches(ROWS) && t.querySelector('.sl-rung__run, .sl-plan');
      return (d && d.getClientRects().length ? d : t).getBoundingClientRect();
    }

    valid(t, top) {
      const r = this.box(t);
      if (!r.height || !r.width || r.right < 0 || r.left > window.innerWidth) return false;
      return r.top >= top && Math.min(r.bottom, window.innerHeight - 24) - r.top >= r.height * 0.8;
    }

    want() {
      const hdr = document.querySelector('.site-header');
      const top = Math.max(0, hdr ? hdr.getBoundingClientRect().bottom : 0);
      if (this.pointed && this.valid(this.pointed, top)) return this.pointed;
      const y = window.innerHeight * 0.45;
      let best = null, bd = Infinity;
      for (const t of this.targets) {
        if (!this.valid(t, top)) continue;
        const r = this.box(t), dist = Math.abs(r.top + r.height / 2 - y);
        if (dist < bd) (bd = dist), (best = t);
      }
      return best;
    }

    group(t) {
      return t && t.matches(ROWS) ? t.closest('.sl-ladder, .sl-pieces') : null;
    }

    size(t) {
      const r = this.box(t), small = window.innerWidth < 768 ? 36 : 48;
      this.style.setProperty('--pw', `${Math.min(window.innerWidth * 0.94, r.width * 1.2 + small).toFixed(1)}px`);
      this.style.setProperty('--ph', `${Math.min(window.innerHeight * 0.7, r.height * 1.4 + small).toFixed(1)}px`);
    }

    iris(frames, dur, done) {
      this.anim?.cancel();
      this.anim = this.pool.animate(frames, { duration: reduce.matches ? 0 : dur, easing: token('--ease-out'), fill: 'forwards' });
      this.anim.onfinish = done;
    }

    arrive(t) {
      const s = this.spot;
      if (s.lit === t) return;
      s.lit?.classList.remove('is-lit');
      s.lit = t;
      t.classList.add('is-lit');
      this.gleam(t.matches(ROWS) ? t.querySelector('.sl-rung__run, .sl-plan') : t);
    }

    irisIn(t) {
      const s = this.spot;
      Object.assign(s, { lead: t, phase: 'in', pending: undefined, from: null });
      this.size(t);
      const r = this.box(t);
      this.place(r.left + r.width / 2, r.top + r.height / 2);
      this.iris([{ opacity: 0, transform: 'scale(.85)' }, { opacity: 1, transform: 'scale(1)' }], 260, () => {
        if (s.lead !== t) return;
        s.phase = 'on';
        this.arrive(t);
      });
    }

    irisOut(next) {
      const s = this.spot;
      s.phase = 'out';
      s.pending = next;
      s.lit?.classList.remove('is-lit');
      s.lit = null;
      this.iris([{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(.6)' }], 180, () => {
        if (s.phase !== 'out') return;
        const n = s.pending;
        Object.assign(s, { lead: null, phase: 'off', pending: undefined });
        if (n) this.irisIn(n);
        this.schedule();
      });
    }

    setLead(t) {
      const s = this.spot;
      if (s.phase === 'out') return void (s.pending = t);
      if (t === s.lead) return;
      if (!s.lead) return void (t && this.irisIn(t));
      if (t && !reduce.matches && s.phase === 'on' && this.group(t) && this.group(t) === this.group(s.lead)) {
        // two rows of one group: the operator pans
        s.lit?.classList.remove('is-lit');
        s.lit = null;
        Object.assign(s, { lead: t, from: { x: s.x, y: s.y }, t0: performance.now() });
        this.size(t);
        return;
      }
      this.irisOut(t || null);
    }

    place(x, y) {
      this.spot.x = x;
      this.spot.y = y;
      this.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
    }

    placeSpot(now) {
      this.setLead(this.want());
      const s = this.spot;
      if (!s.lead) return s.phase === 'out';
      const r = this.box(s.lead);
      let x = r.left + r.width / 2, y = r.top + r.height / 2, moving = s.phase !== 'on';
      if (s.from) {
        const t = clamp((now - s.t0) / 450), k = 1 - Math.pow(1 - t, 3);
        x = s.from.x + (x - s.from.x) * k;
        y = s.from.y + (y - s.from.y) * k;
        if (t < 1) moving = true;
        else {
          s.from = null;
          this.arrive(s.lead);
        }
      }
      this.place(x, y);
      return moving;
    }

    // The gleam: a copy of the drawing, in light, through a narrow slanted window, once.
    gleam(el) {
      if (!el || reduce.matches || el.dataset.gleaming) return;
      const host = el.parentElement;
      const b = el.getBoundingClientRect(), h = host.getBoundingClientRect();
      if (!b.width || !b.height) return;
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      el.dataset.gleaming = '1';
      const wrap = document.createElement('span');
      wrap.className = 'sl-gleam';
      wrap.setAttribute('aria-hidden', 'true');
      Object.assign(wrap.style, { left: `${b.left - h.left}px`, top: `${b.top - h.top}px`, width: `${b.width}px`, height: `${b.height}px` });
      wrap.innerHTML = '<span class="sl-gleam__win"><span class="sl-gleam__img"></span></span>';
      const win = wrap.firstChild, img = win.firstChild, copy = el.cloneNode(true);
      copy.removeAttribute('id');
      copy.removeAttribute('data-lead');
      copy.classList.remove('is-lit', 'sl-lead');
      Object.assign(copy.style, { width: `${b.width}px`, height: `${b.height}px`, margin: '0' });
      img.append(copy);
      host.append(wrap);
      const t = { duration: 600, easing: token('--ease-in-out'), fill: 'both' };
      win.animate([{ transform: 'translateX(-100%)' }, { transform: 'translateX(333.3%)' }], t);
      img.animate([{ transform: 'translateX(30%)' }, { transform: 'translateX(-100%)' }], t).onfinish = () => {
        wrap.remove();
        delete el.dataset.gleaming;
      };
    }

    // ---- One frame loop, only while something moves ----
    frame(now) {
      this.queued = false;
      const vh = window.innerHeight;
      if (!reduce.matches) {
        // the fire room: from the scene's top at 92% of the screen to its centre at the middle,
        // the ray enters and turns (--beam), then the fan leaves the table (--fan)
        this.fires.forEach((f) => {
          const d = f.querySelector('.sl-fire__disp');
          if (!d) return;
          const r = d.getBoundingClientRect(), t = clamp((vh * 0.92 - r.top) / (vh * 0.42 + r.height * 0.5));
          f.style.setProperty('--beam', clamp(t / 0.55).toFixed(3));
          f.style.setProperty('--fan', smooth(clamp((t - 0.5) / 0.45)).toFixed(3));
        });
        // the sunrise plays from the horizon at the screen's foot to the horizon at 40% of it
        this.suns.forEach((st) => {
          const hz = st.getBoundingClientRect().top + (st.slHz || 0);
          st.style.setProperty('--sunrise', smooth(clamp((vh - hz) / (vh * 0.6))).toFixed(3));
        });
      }
      if (this.placeSpot(now)) this.schedule();
    }

    // ---- The sunrise: the sun rises on the i's axis; with a launch date and the countdown on, its
    // height is the countdown (half set 60 days out, a 60th higher each day, in the dot on the day) ----
    placeSky(st) {
      const s = st.getBoundingClientRect();
      const base = st.querySelector('.sl-sun__base'), i = st.querySelector('.sl-sun__i'), mark = st.querySelector('.sl-sun__mark');
      const hz = base ? base.getBoundingClientRect().top - s.top : s.height * 0.62;
      const ir = i && i.getBoundingClientRect();
      const fs = mark ? parseFloat(getComputedStyle(mark).fontSize) : 96;
      const sx = ir ? ir.left - s.left + ir.width / 2 : s.width / 2;
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(st.dataset.launch || '');
      // ponytail: Sydney is +11 in summer (the launch season); a winter date would be an hour out.
      const at = m ? Date.parse(`${m[0]}T00:00:00+11:00`) : NaN;
      const days = isNaN(at) ? null : Math.ceil((at - Date.now()) / 864e5);
      const launched = days !== null && days <= 0, t = days === null ? 0 : clamp(1 - days / 60);
      const dDot = 0.128 * fs, dLow = Math.max(0.32 * fs, window.innerWidth < 768 ? 36 : 48);
      const d = launched ? dDot : dLow + (dDot - dLow) * t;
      const y = launched && ir ? ir.top - s.top + dDot : hz + d * 0.12 * (1 - t);
      const set = (k, v) => st.style.setProperty(k, v);
      st.slHz = hz;
      set('--hz', `${hz.toFixed(1)}px`);
      set('--sun-x', `${sx.toFixed(1)}px`);
      set('--sun-y', `${y.toFixed(1)}px`);
      set('--sun-d', `${d.toFixed(1)}px`);
      set('--sun-drop', `${Math.max(0, hz + d / 2 + 4 - y).toFixed(1)}px`);
      set('--dawn', t.toFixed(3));
      if (mark) mark.style.setProperty('--rim-pos', `${(sx - (mark.getBoundingClientRect().left - s.left) - mark.offsetWidth).toFixed(1)}px`);
      st.toggleAttribute('data-launched', launched);
      const c = st.querySelector('[data-sl-count]');
      if (c) {
        c.hidden = !(days > 0);
        if (days > 0) c.textContent = days === 1 ? c.dataset.one : c.dataset.other.replace(N, days);
      }
    }

    // ---- Clicks: the spec sheet, hearts, Save, close, the hint ----
    click(e) {
      const a = e.target.closest('a[data-sl-sheet]');
      if (a) {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const sec = a.closest('.sl-shop');
        const dlg = sec && sec.querySelector('[data-sl-sheet-dialog]');
        const tpl = sec && sec.querySelector(`template[data-sl-sheet-for="${CSS.escape(a.dataset.slSheet)}"]`);
        if (!dlg || !tpl || typeof dlg.showModal !== 'function') return;
        e.preventDefault();
        dlg.querySelector('[data-sl-sheet-body]').replaceChildren(tpl.content.cloneNode(true));
        this.wish();
        dlg.showModal();
        dlg.addEventListener('close', () => a.focus(), { once: true });
        return;
      }
      const w = e.target.closest('[data-sl-wish]');
      if (w) return void wl()?.toggle(w.dataset.slWish);
      if (e.target.closest('[data-sl-close]') || e.target.matches('[data-sl-sheet-dialog]')) return void e.target.closest('dialog').close();
      const hint = e.target.closest('[data-sl-hint]');
      if (hint) this.hint(hint);
    }

    wish() {
      const list = wl()?.list() || [];
      document.querySelectorAll('[data-sl-wish]').forEach((b) => b.setAttribute('aria-pressed', String(list.includes(b.dataset.slWish))));
      document.querySelectorAll('[data-sl-hint]').forEach((b) => {
        b.disabled = !list.length;
        const s = b.closest('.sl-gift__copy')?.querySelector('[data-sl-hint-status]');
        if (s && !list.length) s.textContent = s.dataset.empty;
        else if (s && s.textContent === s.dataset.empty) s.textContent = '';
      });
    }

    // "Send a hint": the wishlist page's own shared-link format, shared or copied.
    async hint(b) {
      const list = wl()?.list() || [];
      if (!list.length) return;
      const s = b.closest('.sl-gift__copy')?.querySelector('[data-sl-hint-status]');
      const url = new URL(`${window.Shopify?.routes?.root || '/'}pages/wishlist`, location.origin);
      url.searchParams.set('items', list.join(','));
      if (navigator.share) {
        try {
          return void (await navigator.share({ title: document.title, url: url.href }));
        } catch (err) {
          if (err && err.name === 'AbortError') return;
        }
      }
      try {
        await navigator.clipboard.writeText(url.href);
        if (s) s.textContent = s.dataset.copied;
      } catch {
        if (s) s.textContent = url.href;
      }
    }

    // The gift note: the card grows with the note and the hand gets smaller; the count is
    // announced only near the limit.
    count(n) {
      const note = n.closest('.sl-note');
      const c = note?.querySelector('.sl-note__count'), live = note?.querySelector('[data-sl-note-live]');
      if (!c) return;
      const left = n.maxLength - n.value.length;
      n.dataset.len = n.value.length > 90 ? 'l' : 's';
      if (!CSS.supports('field-sizing', 'content')) {
        n.style.height = 'auto';
        n.style.height = `${n.scrollHeight}px`;
      }
      c.textContent = left === 1 ? c.dataset.one : c.dataset.other.replace(N, left);
      if (left > 20) delete n.dataset.said;
      else if (String(left) !== n.dataset.said) {
        live.textContent = c.textContent;
        n.dataset.said = left;
      }
    }
  }

  customElements.define('antarti-spotlight-light', Light);
  if (!document.querySelector('antarti-spotlight-light')) document.body.append(document.createElement('antarti-spotlight-light'));
})();
